import React, { useState, useCallback } from 'react';
import { Button, Alert, Form, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Plus } from 'lucide-react';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useReportDraft, ReportDraftSaveStatus } from '../../hooks/reportDrafts';
import { ReportDraftTextBlock } from '../../types/reportDrafts';
import { sortBlocks } from '../../utils/reportDraftView';
import { exportReportDraftToPDF } from '../../utils/reportDraftPdf';
import ReportDraftItemCard from './ReportDraftItemCard';
import ReportDraftTextBlockEditor from './ReportDraftTextBlockEditor';
import './ReportDraftPage.css';

const SaveStatusBadge: React.FC<{ status: ReportDraftSaveStatus; message?: string | null }> = ({ status, message }) => {
    switch (status) {
        case 'saving':
            return <Badge bg="secondary"><Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />Sparar…</Badge>;
        case 'saved':
            return <Badge bg="success">Sparat</Badge>;
        case 'error':
            return <Badge bg="danger">{message || 'Kunde inte spara — försök igen'}</Badge>;
        default:
            return null;
    }
};

/**
 * Full-page editor for one report draft (admin-only route, plan D9).
 * Everything edited here lives in the draft — the timeline is never written.
 */
const ReportDraftPage: React.FC = () => {
    const { draftKey } = useParams<{ draftKey: string }>();
    const {
        draft,
        renderModel,
        loading,
        error,
        saveStatus,
        saveError,
        updateItem,
        updateItemDebounced,
        saveTextBlock,
        removeTextBlock,
        updateReportTitleDebounced
    } = useReportDraft(draftKey);

    useDocumentTitle(draft ? `${draft.name} - Rapportutkast` : 'Rapportutkast');

    const [exporting, setExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [exportError, setExportError] = useState<string | null>(null);

    /** Next sortOrder at an anchor (append at the end). */
    const nextSortOrder = useCallback((blocks: ReportDraftTextBlock[]): number =>
        blocks.length === 0 ? 0 : Math.max(...blocks.map(b => b.sortOrder)) + 1,
    []);

    const addBlock = useCallback((anchor: Pick<ReportDraftTextBlock, 'anchorType' | 'anchorYear' | 'anchorEventId'>, siblings: ReportDraftTextBlock[]) => {
        saveTextBlock({
            text: '',
            ...anchor,
            sortOrder: nextSortOrder(siblings)
        }, false);
    }, [saveTextBlock, nextSortOrder]);

    /** Move a block one step within its anchor: renumber the sibling list. */
    const moveBlock = useCallback((siblings: ReportDraftTextBlock[], index: number, direction: -1 | 1) => {
        const reordered = [...siblings];
        const [moved] = reordered.splice(index, 1);
        reordered.splice(index + direction, 0, moved);
        reordered.forEach((block, position) => {
            if (block.sortOrder !== position) {
                saveTextBlock({ ...block, sortOrder: position }, false);
            }
        });
    }, [saveTextBlock]);

    const blockList = (blocks: ReportDraftTextBlock[]) => {
        const sorted = sortBlocks(blocks);
        return sorted.map((block, index) => (
            <ReportDraftTextBlockEditor
                key={block.key}
                block={block}
                onTextChange={(text) => saveTextBlock({ ...block, text }, true)}
                onRemove={() => removeTextBlock(block.key)}
                onMoveUp={index > 0 ? () => moveBlock(sorted, index, -1) : undefined}
                onMoveDown={index < sorted.length - 1 ? () => moveBlock(sorted, index, 1) : undefined}
            />
        ));
    };

    const handleDownload = useCallback(async () => {
        if (!renderModel || !draft) return;
        setExporting(true);
        setExportProgress(0);
        setExportError(null);
        try {
            await exportReportDraftToPDF({
                model: renderModel,
                reportTitle: draft.reportTitle,
                onProgress: setExportProgress
            });
        } catch (err: any) {
            console.error('PDF export failed:', err);
            setExportError(err.message || 'Kunde inte skapa PDF:en. Försök igen.');
        } finally {
            setExporting(false);
        }
    }, [renderModel, draft]);

    if (loading) {
        return (
            <div className="report-draft-page">
                <div className="report-draft-page__content">
                    <SectionLoader />
                </div>
            </div>
        );
    }

    if (error || !draft || !renderModel) {
        return (
            <div className="report-draft-page">
                <div className="report-draft-page__content">
                    <Alert variant="danger">{error || 'Utkastet hittades inte'}</Alert>
                    <Link to="/timeline" className="btn btn-outline-primary">
                        <ArrowLeft size={14} className="me-2" />
                        Till tidslinjen
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="report-draft-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={[
                        { text: 'Tidslinje', link: '/timeline' },
                        { text: draft.name }
                    ]}
                />
            </div>
            <div className="report-draft-page__content">

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                    <h4 className="mb-0">{draft.name}</h4>
                    <SaveStatusBadge status={saveStatus} message={saveError} />
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <Button variant="primary" onClick={handleDownload} disabled={exporting}>
                        <Download size={14} className="me-2" />
                        {exporting ? 'Skapar rapport…' : 'Ladda ner PDF'}
                    </Button>
                </div>
            </div>

            {exporting && <ProgressBar now={exportProgress} className="mb-3" animated />}
            {exportError && (
                <Alert variant="danger" dismissible onClose={() => setExportError(null)}>
                    {exportError}
                </Alert>
            )}

            <Form.Group className="mb-4" style={{ maxWidth: '480px' }}>
                <Form.Label className="small text-muted mb-1">Rapportens titel</Form.Label>
                <Form.Control
                    type="text"
                    value={draft.reportTitle}
                    onChange={(e) => updateReportTitleDebounced(e.target.value)}
                />
            </Form.Group>


            <section className="mb-4">
                <h5>Inledning</h5>
                {blockList(renderModel.documentBlocks)}
                <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => addBlock({ anchorType: 'document' }, renderModel.documentBlocks)}
                >
                    <Plus size={14} className="me-1" />
                    Lägg till inledande stycke
                </Button>
            </section>

            {renderModel.years.map(year => (
                <section key={year.year} className="mb-4">
                    <div className="d-flex align-items-center justify-content-between border-bottom pb-1 mb-2">
                        <h5 className="mb-0">{year.year}</h5>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => addBlock({ anchorType: 'year', anchorYear: year.year }, year.blocks)}
                        >
                            <Plus size={14} className="me-1" />
                            Stycke för året
                        </Button>
                    </div>
                    {blockList(year.blocks)}
                    {year.events.map(rendered => (
                        <React.Fragment key={rendered.eventId}>
                            <ReportDraftItemCard
                                rendered={rendered}
                                onChange={(item, debounce) =>
                                    debounce
                                        ? updateItemDebounced(rendered.eventId, item)
                                        : updateItem(rendered.eventId, item)
                                }
                                onAddParagraphAfter={() =>
                                    addBlock({ anchorType: 'event', anchorEventId: rendered.eventId }, rendered.blocksAfter)
                                }
                            />
                            <div className={`report-draft-after-blocks ${rendered.included ? '' : 'report-draft-after-blocks-excluded'}`}>
                                {!rendered.included && rendered.blocksAfter.length > 0 && (
                                    <div className="small text-muted fst-italic mb-1">
                                        Händelsen är utesluten — stycken under den skrivs inte ut.
                                    </div>
                                )}
                                {blockList(rendered.blocksAfter)}
                            </div>
                        </React.Fragment>
                    ))}
                </section>
            ))}

            </div>
        </div>
    );
};

export default ReportDraftPage;
